import { useEffect, useMemo, useState } from "react";
import type { i18n as I18nextInstance } from "i18next";
import { type Locale, useGetEnabledLocales } from "src/api/locales/useLocales";

export type ConsentContentLocale = Locale & { code: string };

export const useConsentGovernanceLocales = (i18n: I18nextInstance) => {
    const initialLocale = i18n.resolvedLanguage || i18n.language || "en";
    const [selectedLocale, setSelectedLocale] = useState(initialLocale);
    const { data: enabledLocales = [], isLoading: isLocalesLoading } = useGetEnabledLocales();

    const availableContentLocales = useMemo(
        () =>
            enabledLocales.filter(
                (locale): locale is ConsentContentLocale =>
                    typeof locale.code === "string" && locale.code.trim().length > 0,
            ),
        [enabledLocales],
    );

    const selectedLocaleMeta = useMemo(
        () => availableContentLocales.find((locale) => locale.code === selectedLocale) ?? null,
        [availableContentLocales, selectedLocale],
    );

    useEffect(() => {
        if (availableContentLocales.length === 0) {
            return;
        }

        if (availableContentLocales.some((locale) => locale.code === selectedLocale)) {
            return;
        }

        const preferredLocale = [i18n.resolvedLanguage, i18n.language].find((locale) =>
            availableContentLocales.some((availableLocale) => availableLocale.code === locale),
        );

        setSelectedLocale(preferredLocale ?? availableContentLocales[0].code);
    }, [availableContentLocales, i18n.language, i18n.resolvedLanguage, selectedLocale]);

    return {
        availableContentLocales,
        isLocalesLoading,
        selectedLocale,
        selectedLocaleMeta,
        setSelectedLocale,
        uiLocale: initialLocale,
    };
};
